/**
 * AccountantAnomalyReview - Standalone anomaly review page for accountants
 *
 * Fetches anomaly data from the dashboard API and renders the AnomalyResolver
 * component in an accountant-appropriate context (not inside the wizard).
 *
 * Accountant-specific features:
 * - Approve / reject existing resolutions
 * - Add forensic notes
 * - Flag anomalies for follow-up
 * - Read-only on underlying transaction data
 *
 * Route: /accountant/anomalies/:reportId
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAccountantAuth } from '../../contexts/AccountantAuthContext';
import { useAuth, useUser } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import { humanizeLabel } from '../../utils/formatters';
import AnomalyResolver from '../wizard/AnomalyResolver';
import { WhyFlaggedDrawer } from '../explainability/WhyFlaggedDrawer';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  CheckCircle,
  XCircle,
  MessageSquare,
  Flag,
  RefreshCw,
  Clock,
} from 'lucide-react';

export interface AnomalyReviewContentProps {
  reportId?: string;
  showHeader?: boolean;
}

interface AccountantAction {
  anomaly_id: string;
  action_type: 'approve_resolution' | 'reject_resolution' | 'add_note' | 'flag';
  note: string;
  performed_by_name?: string;
  performed_at?: string;
}

interface AnomalyReviewData {
  success: boolean;
  report_id: string;
  business_name: string;
  anomalies: any[];
  periods_data: { month: string; revenue: number }[];
  total_transactions: number;
  total_months: number;
  unresolved_count: number;
  high_priority_count: number;
  resolved_count: number;
  loss_event_start?: string;
  loss_event_end?: string;
  has_cached_analysis: boolean;
  has_periods: boolean;
  error?: string;
}

export function AnomalyReviewContent({ reportId: reportIdProp, showHeader = true }: AnomalyReviewContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;
  const navigate = useNavigate();
  const { accountant, loading: authLoading, getAuthHeader } = useAccountantAuth();
  const { getToken } = useAuth();
  // user lives on useUser(), not useAuth() — see AttorneyQueue note.
  const { user } = useUser();

  // Dual-auth: support both AccountantAuth (external) and Clerk/TestAuth (internal)
  const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';

  const testAccountant = useMemo(() => ({
    id: 'test_user_123',
    email: 'test@claimsync.com',
    full_name: 'Test Accountant'
  }), []);

  // Defensive fallback chain that supports both Clerk's user shape
  // (id, primaryEmailAddress.emailAddress, fullName) and the test-mode
  // mock shape (sub, userId, name, email). Cast to any for the chain
  // per archaeology pattern §7 — strict union narrowing isn't worth
  // the noise for a 3-line lookup.
  const clerkAccountant = useMemo(() => user ? ({
    id: (user as any).sub || (user as any).userId || user.id || 'clerk-user',
    email: (user as any).email || (user as any).primaryEmailAddress?.emailAddress || '',
    full_name: (user as any).name || user.fullName || (user as any).email || 'Staff'
  }) : null, [user]);

  const effectiveAccountant = isTestMode ? testAccountant : (accountant || clerkAccountant);
  const effectiveAuthLoading = isTestMode ? false : (authLoading && !user);

  const [reviewData, setReviewData] = useState<AnomalyReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLog, setActionLog] = useState<AccountantAction[]>([]);
  const [savingAction, setSavingAction] = useState<string | null>(null);

  // WhyFlaggedDrawer state
  const [explainDrawerOpen, setExplainDrawerOpen] = useState(false);
  const [selectedAnomalyId, setSelectedAnomalyId] = useState<string | null>(null);

  // Build auth headers supporting both auth contexts
  const getHeaders = useCallback(async () => {
    // Try AccountantAuth first (external accountant JWT)
    const accountantHeader = getAuthHeader();
    if (accountantHeader && (accountantHeader as any).Authorization) {
      return accountantHeader;
    }
    // Fall back to Clerk/TestAuth token
    try {
      const token = await getToken();
      if (token) return { Authorization: `Bearer ${token}` };
    } catch {}
    return {};
  }, [getAuthHeader, getToken]);

  const fetchReviewData = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const data = await api.get<AnomalyReviewData>(
        `/api/v1/dashboard/anomaly-review/${reportId}`,
        { headers, showErrorToast: false }
      );
      if (data.success === false && data.error) {
        setError(data.error);
      }
      setReviewData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load anomaly data');
    } finally {
      setLoading(false);
    }
  }, [reportId, getHeaders]);

  useEffect(() => {
    if (effectiveAuthLoading) return;
    if (!effectiveAccountant) {
      navigate('/accountant/login');
      return;
    }
    fetchReviewData();
  }, [fetchReviewData, effectiveAccountant, effectiveAuthLoading]);

  const saveAccountantAction = async (
    anomalyId: string,
    actionType: AccountantAction['action_type'],
    note: string
  ) => {
    if (!reportId) return;
    setSavingAction(anomalyId);
    try {
      const headers = await getHeaders();
      await api.post(
        `/api/v1/dashboard/anomaly-review/${reportId}/accountant-action`,
        { anomaly_id: anomalyId, action_type: actionType, note },
        { headers, showErrorToast: false }
      );
      setActionLog(prev => [...prev, {
        anomaly_id: anomalyId,
        action_type: actionType,
        note,
        performed_at: new Date().toISOString(),
      }]);
      // Refresh data to reflect changes
      await fetchReviewData();
    } catch (err) {
      console.error('Failed to save action:', err);
    } finally {
      setSavingAction(null);
    }
  };

  // Handlers for AnomalyResolver callbacks
  const handleResolve = (anomalyId: string, reason: string, note: string) => {
    // Accountant resolving = approving with their reason
    saveAccountantAction(anomalyId, 'approve_resolution', `${reason}: ${note}`);
  };

  const handleBatchResolve = (anomalyIds: string[], reason: string, note: string) => {
    anomalyIds.forEach(id => {
      saveAccountantAction(id, 'approve_resolution', `${reason}: ${note}`);
    });
  };

  // Handler for opening SHAP explainability drawer
  const handleExplainAnomaly = (anomalyId: string) => {
    setSelectedAnomalyId(anomalyId);
    setExplainDrawerOpen(true);
  };

  const handleCloseExplainDrawer = () => {
    setExplainDrawerOpen(false);
    // Small delay to clear the anomaly ID after close animation
    setTimeout(() => setSelectedAnomalyId(null), 300);
  };

  const getActionCountForAnomaly = (anomalyId: string) =>
    actionLog.filter(a => a.anomaly_id === anomalyId).length;

  // Summary stats
  const totalAnomalies = reviewData?.anomalies?.length || 0;
  const unresolvedCount = reviewData?.unresolved_count || 0;
  const resolvedCount = reviewData?.resolved_count || 0;
  const approvedCount = reviewData?.anomalies?.filter(
    (a: any) => a.accountant_approved || actionLog.some(
      act => act.anomaly_id === a.id && act.action_type === 'approve_resolution'
    )
  ).length || 0;

  if (effectiveAuthLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: 'var(--color-brand)' }} />
          <p className="cs-meta">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      {showHeader && (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-[2px] transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            title="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="cs-eyebrow flex items-center gap-2 mb-1">
              <span>ACCOUNTANT REVIEW</span>
              <span>•</span>
              <span>ANOMALY ANALYSIS</span>
            </div>
            <h1 className="cs-display">
              {reviewData?.business_name || 'Anomaly Review'}
            </h1>
          </div>
          <button
            onClick={fetchReviewData}
            disabled={loading}
            className="cs-label flex items-center gap-2 px-4 py-2 rounded-[2px] disabled:opacity-50"
            style={{
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      )}

      {/* Status Summary Bar */}
      {reviewData && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-[2px] p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <p className="cs-figure-lg">{totalAnomalies}</p>
                <p className="cs-meta">Total Anomalies</p>
              </div>
            </div>
          </div>
          <div className="rounded-[2px] p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5" style={{ color: unresolvedCount > 0 ? 'var(--color-blocker)' : 'var(--color-text-muted)' }} />
              <div>
                <p className="cs-figure-lg">{unresolvedCount}</p>
                <p className="cs-meta">Unresolved</p>
              </div>
            </div>
          </div>
          <div className="rounded-[2px] p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <div>
                <p className="cs-figure-lg">{resolvedCount}</p>
                <p className="cs-meta">Resolved</p>
              </div>
            </div>
          </div>
          <div className="rounded-[2px] p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <div>
                <p className="cs-figure-lg">{approvedCount}</p>
                <p className="cs-meta">Approved</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="rounded-[2px] p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: 'var(--color-brand)' }} />
          <p className="cs-body" style={{ fontWeight: 500 }}>Loading anomaly data...</p>
          <p className="cs-meta mt-1">Fetching anomalies and resolution history</p>
        </div>
      ) : error ? (
        <div className="rounded-[2px] p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--color-blocker)' }} />
          <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>Failed to load anomaly data</p>
          <p className="cs-meta mt-1">{error}</p>
          <button
            onClick={fetchReviewData}
            className="cs-label mt-4 px-4 py-2 rounded-[2px]"
            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            Retry
          </button>
        </div>
      ) : reviewData && totalAnomalies === 0 ? (
        <div className="rounded-[2px] p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          {reviewData.has_cached_analysis ? (
            <>
              {/* Detection RAN and found nothing — a real clean result. */}
              <ShieldCheck className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-verified)' }} />
              <p className="cs-h3">No Anomalies Detected</p>
              <p className="cs-meta mt-1">Anomaly detection ran on this case and found no anomalies.</p>
              <button
                onClick={() => navigate(-1)}
                className="cs-label mt-4 px-4 py-2 rounded-[2px]"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                Back to Dashboard
              </button>
            </>
          ) : (
            <>
              {/* Detection has NOT run — hueless, honest, and points to the fix. */}
              <Clock className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-muted)' }} />
              <p className="cs-h3">Anomaly Detection Not Run</p>
              <p className="cs-meta mt-1">
                {reviewData.has_periods
                  ? 'Anomaly detection runs on engine output. Complete Engines (Step 3), then return here.'
                  : 'No financial data is available for anomaly detection.'}
              </p>
              {reviewData.has_periods && (
                <button
                  onClick={() => navigate(`/accountant/reports/${reportId}/engines`)}
                  className="cs-label mt-4 px-4 py-2 rounded-[2px]"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  Go to Engines (Step 3)
                </button>
              )}
              <button
                onClick={() => navigate(-1)}
                className="cs-label mt-4 ml-2 px-4 py-2 rounded-[2px]"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                Back to Dashboard
              </button>
            </>
          )}
        </div>
      ) : reviewData ? (
        <div className="space-y-6">
          {/* Accountant Action Quick Bar */}
          {unresolvedCount > 0 && (
            <div
              className="rounded-[2px] p-4 flex items-center gap-4"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
              <div className="flex-1">
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                  {unresolvedCount} anomal{unresolvedCount === 1 ? 'y' : 'ies'} pending review
                </p>
                <p className="cs-meta">
                  Review each anomaly and either approve the resolution, add forensic notes, or flag for follow-up.
                </p>
              </div>
            </div>
          )}

          {/* Session Action Log */}
          {actionLog.length > 0 && (
            <div
              className="rounded-[2px] p-4"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                  Session Actions ({actionLog.length})
                </p>
              </div>
              <div className="space-y-1">
                {actionLog.slice(-5).map((action, i) => (
                  <div key={i} className="cs-meta flex items-center gap-2">
                    {action.action_type === 'approve_resolution' && <CheckCircle className="w-3 h-3" style={{ color: 'var(--color-verified)' }} />}
                    {action.action_type === 'reject_resolution' && <XCircle className="w-3 h-3" style={{ color: 'var(--color-blocker)' }} />}
                    {action.action_type === 'add_note' && <MessageSquare className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />}
                    {action.action_type === 'flag' && <Flag className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />}
                    <span>
                      {humanizeLabel(action.action_type)} on anomaly ...{action.anomaly_id.slice(-6)}
                      {action.note && ` — "${action.note.slice(0, 60)}${action.note.length > 60 ? '...' : ''}"`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AnomalyResolver Component - reused from wizard */}
          <AnomalyResolver
            anomalies={reviewData.anomalies.map((a: any) => ({
              id: a.id || a.anomaly_id || `anm-${Math.random().toString(36).slice(2, 8)}`,
              severity: a.severity || 'medium',
              type: a.anomaly_type || a.type || 'revenue_change',
              title: a.title || `${humanizeLabel(a.anomaly_type) || 'Anomaly'} — ${a.affected_period || 'Unknown period'}`,
              description: a.description || a.explanation || '',
              percent: a.deviation_percent || a.percent || 0,
              // Revenue-change anomalies carry the from/to figures as
              // prev/curr_period_revenue (the canonical detector's fields); without
              // these the card showed "$0 → $0" beside a real "-100%" drop.
              from: a.from_value ?? a.from ?? a.prev_period_revenue ?? 0,
              to: a.to_value ?? a.to ?? a.curr_period_revenue ?? 0,
              // Only revenue spikes/drops have from→to semantics; duplicate/COGS/
              // Benford/utility_gap don't (their from/to are absent → coerced to 0).
              // Flag it so the card suppresses the meaningless "$0 → $0" line and
              // falls back to related_amount where present.
              hasDelta:
                (a.from_value ?? a.from ?? a.prev_period_revenue) != null &&
                (a.to_value ?? a.to ?? a.curr_period_revenue) != null,
              relatedAmount: a.related_amount ?? null,
              periodFrom: a.period_from || a.periodFrom || '',
              periodTo: a.period_to || a.periodTo || '',
              period: a.affected_period || a.period || '',
              deviation: a.deviation_percent || a.deviation || 0,
              categories: a.category_breakdown || a.categories || [],
              transactions: a.top_transactions || a.transactions || [],
              aiSuggestion: a.ai_analysis?.explanation || a.aiSuggestion || a.explanation || '',
              aiConfidence: a.ai_analysis?.confidence || a.aiConfidence,
              sourceDoc: a.source_document || a.sourceDoc,
              lossEvent: a.is_loss_period || a.lossEvent,
              is_resolved: a.is_resolved || false,
              resolution_note: a.resolution_note || '',
              resolved_at: a.resolved_at || '',
              resolution_reason: a.resolution_reason || '',
              isPreLoss: a.is_pre_loss || a.isPreLoss,
              isLossPeriod: a.is_loss_period || a.isLossPeriod,
              isPostLoss: a.is_post_loss || a.isPostLoss,
              forensic_insight: a.forensic_insight,
              forensic_analysis: a.forensic_analysis,
              accountant_review: a.accountant_review,
            }))}
            periodsData={reviewData.periods_data}
            totalTransactions={reviewData.total_transactions}
            totalMonths={reviewData.total_months}
            lossEventStart={reviewData.loss_event_start}
            lossEventEnd={reviewData.loss_event_end}
            onResolve={handleResolve}
            onBatchResolve={handleBatchResolve}
            onExplainAnomaly={handleExplainAnomaly}
            loading={false}
          />

          {/* SHAP Explainability Drawer */}
          {selectedAnomalyId && (
            <WhyFlaggedDrawer
              isOpen={explainDrawerOpen}
              onClose={handleCloseExplainDrawer}
              anomalyId={selectedAnomalyId}
              reportId={reportId}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

export default AnomalyReviewContent;

